import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
const DEFAULT_OFFSET = 8;
const DEFAULT_MARGIN = 8;
const DEFAULT_POPOVER_WIDTH = 264;
export function useClickPositionedPopover(params = {}) {
    const { onRequestClose, closeOnOutsideClick = true, closeOnEscape = true, offset = DEFAULT_OFFSET, margin = DEFAULT_MARGIN, popoverWidth = DEFAULT_POPOVER_WIDTH, popoverHeight, } = params;
    const popoverRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const [placement, setPlacement] = useState('bottom-center');
    const clickPositionRef = useRef(null);
    const calculateVerticalPlacement = useCallback((spaceBelow, spaceAbove, estimatedHeight) => {
        const needsVerticalSpace = estimatedHeight + offset + margin;
        if (spaceBelow >= needsVerticalSpace) {
            return 'bottom';
        }
        if (spaceAbove >= needsVerticalSpace) {
            return 'top';
        }
        return spaceBelow >= spaceAbove ? 'bottom' : 'top';
    }, [offset, margin]);
    const calculateHorizontalPlacement = useCallback((clickX, measuredWidth, viewportWidth, spaceRight, spaceLeft) => {
        const needsHorizontalSpace = measuredWidth + margin * 2;
        const centeredLeft = clickX - measuredWidth / 2;
        if (centeredLeft >= margin && centeredLeft + measuredWidth <= viewportWidth - margin) {
            return { horizontalPlacement: 'center', left: centeredLeft };
        }
        if (spaceRight >= needsHorizontalSpace) {
            return { horizontalPlacement: 'left', left: clickX + offset };
        }
        if (spaceLeft >= needsHorizontalSpace) {
            return { horizontalPlacement: 'right', left: clickX - measuredWidth - offset };
        }
        const useLeft = spaceRight >= spaceLeft;
        const leftPosition = useLeft
            ? Math.max(margin, Math.min(clickX + offset, viewportWidth - measuredWidth - margin))
            : Math.max(margin, Math.min(clickX - measuredWidth - offset, viewportWidth - measuredWidth - margin));
        return {
            horizontalPlacement: useLeft ? 'left' : 'right',
            left: leftPosition,
        };
    }, [offset, margin]);
    const calculatePosition = useCallback(() => {
        if (!popoverRef.current || !clickPositionRef.current)
            return;
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
        const popoverRect = popoverRef.current.getBoundingClientRect();
        const measuredWidth = popoverRect.width || popoverWidth;
        const measuredHeight = popoverRect.height || popoverHeight || 0;
        const { x: clickX, y: clickY } = clickPositionRef.current;
        const spaceBelow = viewportHeight - clickY;
        const spaceAbove = clickY;
        const spaceRight = viewportWidth - clickX;
        const spaceLeft = clickX;
        const estimatedHeight = measuredHeight > 0 ? measuredHeight : 273;
        const verticalPlacement = calculateVerticalPlacement(spaceBelow, spaceAbove, estimatedHeight);
        const { horizontalPlacement, left: leftPosition } = calculateHorizontalPlacement(clickX, measuredWidth, viewportWidth, spaceRight, spaceLeft);
        const top = verticalPlacement === 'bottom' ? clickY + offset : clickY - estimatedHeight - offset;
        const constrainedTop = Math.max(margin, Math.min(top, viewportHeight - estimatedHeight - margin));
        const constrainedLeft = Math.max(margin, Math.min(leftPosition, viewportWidth - measuredWidth - margin));
        const finalPlacement = `${verticalPlacement}-${horizontalPlacement}`;
        setPosition({ top: constrainedTop, left: constrainedLeft });
        setPlacement(finalPlacement);
    }, [offset, margin, popoverWidth, popoverHeight, calculateVerticalPlacement, calculateHorizontalPlacement]);
    const openPopover = useCallback((event) => {
        clickPositionRef.current = { x: event.clientX, y: event.clientY };
        setIsOpen(true);
    }, []);
    const closePopover = useCallback(() => {
        setIsOpen(false);
        clickPositionRef.current = null;
        onRequestClose?.();
    }, [onRequestClose]);
    const togglePopover = useCallback((event) => {
        if (isOpen) {
            closePopover();
        }
        else {
            openPopover(event);
        }
    }, [isOpen, openPopover, closePopover]);
    useLayoutEffect(() => {
        if (isOpen) {
            const timeoutId = setTimeout(() => {
                calculatePosition();
            }, 0);
            return () => clearTimeout(timeoutId);
        }
    }, [isOpen, calculatePosition]);
    useEffect(() => {
        if (!isOpen)
            return;
        const handleResize = () => {
            calculatePosition();
        };
        const handleScroll = () => {
            calculatePosition();
        };
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [isOpen, calculatePosition]);
    useEffect(() => {
        if (!onRequestClose || !isOpen || !closeOnOutsideClick)
            return;
        const handleMouseDown = (e) => {
            const target = e.target;
            const popover = popoverRef.current;
            if (!popover || !target)
                return;
            const targetElement = target instanceof Element ? target : null;
            if (targetElement?.closest('[data-ibk-outside-click-ignore="true"]'))
                return;
            if (popover.contains(target))
                return;
            const clickPos = clickPositionRef.current;
            if (clickPos) {
                const elementAtClick = document.elementFromPoint(clickPos.x, clickPos.y);
                if (elementAtClick && popover.contains(elementAtClick))
                    return;
            }
            closePopover();
        };
        document.addEventListener('mousedown', handleMouseDown, true);
        return () => {
            document.removeEventListener('mousedown', handleMouseDown, true);
        };
    }, [onRequestClose, isOpen, closeOnOutsideClick, closePopover]);
    useEffect(() => {
        if (!onRequestClose || !isOpen || !closeOnEscape)
            return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                closePopover();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onRequestClose, isOpen, closeOnEscape, closePopover]);
    return {
        popoverRef: popoverRef,
        position,
        placement,
        isOpen,
        openPopover,
        closePopover,
        togglePopover,
    };
}
